import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";

const toForm = (d) => ({
  enabled: d.enabled, days: d.days.join(", "), extra: d.extra_recipients.join(", "),
  escOn: !!d.escalation_enabled, esc: (d.escalation_recipients || []).join(", "),
});
const emails = (v) => v.split(/[\s,;]+/).filter(Boolean);

export function IncidentReminderSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => {
    api.get("/incidents/reminders/settings").then(({ data }) => setS(toForm(data))).catch(() => {});
  }, []);
  if (!s) return null;

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.put("/incidents/reminders/settings", {
        enabled: s.enabled,
        days: s.days.split(/[\s,]+/).filter(Boolean),
        extra_recipients: emails(s.extra),
        escalation_enabled: s.escOn,
        escalation_recipients: emails(s.esc),
      });
      setS(toForm(data));
      toast.success("Reminder settings saved.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed."); }
    setBusy("");
  };
  const runNow = async () => {
    setBusy("run");
    try {
      const { data } = await api.post("/incidents/reminders/run-now");
      toast.success(data.incidents || data.escalation_sent
        ? `Sent ${data.emails} reminder email(s)${data.escalation_sent ? ` and an escalation digest of ${data.overdue} overdue incident(s)` : ""}.`
        : "No reminders due right now.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Run failed."); }
    setBusy("");
  };

  return (
    <div className="border-t border-border pt-4 space-y-3" data-testid="incident-reminder-settings">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold">Incident deadline reminder emails</Label>
        <Switch checked={s.enabled} onCheckedChange={(v) => setS((p) => ({ ...p, enabled: v }))} data-testid="reminder-enabled" />
      </div>
      <div>
        <Label className="text-[11px]">Days before due date (comma-separated)</Label>
        <Input value={s.days} onChange={(e) => setS((p) => ({ ...p, days: e.target.value }))} className="rounded-none mt-1 w-40" data-testid="reminder-days" />
      </div>
      <div>
        <Label className="text-[11px]">Extra recipients (optional)</Label>
        <Input value={s.extra} onChange={(e) => setS((p) => ({ ...p, extra: e.target.value }))} placeholder="compliance.manager@utility.com" className="rounded-none mt-1" data-testid="reminder-extra" />
      </div>
      <p className="text-[11px] text-muted-foreground">
        Sent daily to the incident Owner and the linked Lead Investigator (or every Lead Investigator when no investigation is linked).
        Each reminder goes out once per due date; extending the deadline restarts them.
      </p>
      <div className="border border-red-200 bg-red-50/40 p-3 space-y-2" data-testid="escalation-settings">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-semibold">Daily overdue escalation digest</Label>
          <Switch checked={s.escOn} onCheckedChange={(v) => setS((p) => ({ ...p, escOn: v }))} data-testid="escalation-enabled" />
        </div>
        <Input value={s.esc} onChange={(e) => setS((p) => ({ ...p, esc: e.target.value }))} placeholder="compliance.manager@utility.com, vp.ops@utility.com" className="rounded-none" data-testid="escalation-recipients" />
        <p className="text-[11px] text-muted-foreground">One email per day listing every incident past its due date (days overdue, owner, classification), sent to whoever you add here.</p>
      </div>
      <div className="flex gap-2">
        <Button size="sm" className="rounded-none" onClick={save} disabled={!!busy} data-testid="reminder-save">{busy === "save" ? "Saving…" : "Save reminders & escalation"}</Button>
        <Button size="sm" variant="outline" className="rounded-none" onClick={runNow} disabled={!!busy} data-testid="reminder-run-now">{busy === "run" ? "Sending…" : "Send due reminders now"}</Button>
      </div>
    </div>
  );
}
